import React, { useState, useEffect, useMemo } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import {
  useGetModelQuery,
  useFineTuneModelMutation,
  useFetchModelsQuery,
} from '../../../../services/modelsApi'
import { selectSelectedModelId } from '../../../../store/slices/modelSlice'
import {
  selectSelectedTrainingSource,
  selectSelectedValidationSource,
} from '../../../../store/slices/datasetSlice'
import styled from 'styled-components'
import ErrorDialog from '../../../modals/ErrorDialog'
import { useGetAnnotationStatisticsQuery } from '../../../../services/annotationApi'
import SelectedTrainingSourceCard from '../../../SelectedTrainingSourceCard'
import SelectedValidationSourceCard from '../../../SelectedValidationSourceCard'
import {
  Model,
  TrainingJobEntryBBox,
  TrainingJobEntryClassification,
} from '../../../../types/models/model'
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaSpinner,
  FaCheck,
  FaDatabase,
  FaChartBar,
  FaBrain,
  FaHistory,
  FaTrophy,
  FaLayerGroup,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
} from 'react-icons/fa'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
`

const FineTuneButton = styled.button<{
  $isRetraining?: boolean
  $canStart?: boolean
}>`
  width: 100%;
  padding: 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  ${(props: { $isRetraining?: boolean; $canStart?: boolean }) => {
    if (!props.$canStart) {
      return `
        background: rgba(255, 255, 255, 0.1);
        color: #8c8c8c;
        cursor: not-allowed;
      `
    }

    if (props.$isRetraining) {
      return `
        background: linear-gradient(135deg, #ffc107 0%, #f39c12 100%);
        color: #1a1a1a;
        box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
      `
    }

    return `
      background: linear-gradient(135deg, #9333ea 0%, #c084fc 100%);
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(147, 51, 234, 0.3);
    `
  }}

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${(props: { $isRetraining?: boolean }) => {
      if (props.$isRetraining) return '0 6px 16px rgba(255, 193, 7, 0.4)'
      return '0 6px 16px rgba(147, 51, 234, 0.4)'
    }};

    &::after {
      opacity: 1;
    }
  }

  svg {
    font-size: 1rem;
  }
`

// Tab Interface Styled Components
const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const TabNavigation = styled.div`
  display: flex;
  gap: 0.5rem;
  border-bottom: 1px solid rgba(147, 51, 234, 0.2);
  padding-bottom: 0.5rem;
`

const TabButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${(props) => (props.$active ? 'rgba(147, 51, 234, 0.2)' : 'transparent')};
  border: 1px solid
    ${(props) => (props.$active ? 'rgba(147, 51, 234, 0.3)' : 'rgba(147, 51, 234, 0.1)')};
  border-radius: 6px;
  color: ${(props) => (props.$active ? '#ffffff' : '#b0b0b0')};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  font-weight: 500;

  &:hover {
    background: rgba(147, 51, 234, 0.1);
    color: #ffffff;
    border-color: rgba(147, 51, 234, 0.2);
  }

  svg {
    font-size: 0.9rem;
  }
`

const TabContent = styled.div`
  min-height: 200px;
`

const FineTunePhase: React.FC = () => {
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [selectedTrainingSources, setSelectedTrainingSources] = useState<string[]>([])
  const [selectedValidationSources, setSelectedValidationSources] = useState<string[]>([])
  const [activeMainTab, setActiveMainTab] = useState<'model-data' | 'training'>('model-data')
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const currentTrainingSources = useAppSelector(selectSelectedTrainingSource)
  const currentValidationSources = useAppSelector(selectSelectedValidationSource)

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const { data: model } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId, pollingInterval: 10000 }
  )
  const [fineTuneModel, { isLoading: isRetraining }] = useFineTuneModelMutation()

  // Get child models (models that have parent_id set to selectedModelId) and sort by created_at
  const sortedChildModels = useMemo(() => {
    const children = modelsData?.models?.filter((m) => m.parent_id === selectedModelId) || []
    return [...children].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
  }, [modelsData?.models, selectedModelId])

  // Get the latest model (either the latest child model or the parent model)
  const latestModel = useMemo(() => {
    return sortedChildModels[0] || model
  }, [sortedChildModels, model])

  // Check if the latest model is currently training
  const isCurrentlyTraining = useMemo(() => {
    // Check the latest model (which should be the most recent fine-tuning iteration)
    // Check for various possible training states (the API uses 'state' not 'status')
    const trainingStates = ['training', 'pending', 'queued', 'in_progress', 'running']
    return latestModel && trainingStates.includes(latestModel.state?.toLowerCase() || '')
  }, [latestModel])

  // Helper function to calculate F1 score
  const calculateF1Score = (precision: any, recall: any): string => {
    if (!precision || !recall) return '-'

    const p = typeof precision === 'number' ? precision : parseFloat(String(precision))
    const r = typeof recall === 'number' ? recall : parseFloat(String(recall))

    if (isNaN(p) || isNaN(r) || p === 0 || r === 0) return '-'

    const f1 = (2 * p * r) / (p + r)
    return (f1 * 100).toFixed(1)
  }

  // Helper function to get F1 score as number for comparison
  const getF1ScoreValue = (precision: any, recall: any): number => {
    if (!precision || !recall) return 0

    const p = typeof precision === 'number' ? precision : parseFloat(String(precision))
    const r = typeof recall === 'number' ? recall : parseFloat(String(recall))

    if (isNaN(p) || isNaN(r) || p === 0 || r === 0) return 0

    return (2 * p * r) / (p + r)
  }

  // Helper function to render trend indicator
  const renderTrendIndicator = (currentModel: Model, previousModel: Model | null) => {
    if (!previousModel) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#666666',
          }}
        >
          <FaMinus size={12} />
        </div>
      )
    }

    const currentF1 = getF1ScoreValue(
      currentModel.metrics?.['validation:precision'],
      currentModel.metrics?.['validation:recall']
    )
    const previousF1 = getF1ScoreValue(
      previousModel.metrics?.['validation:precision'],
      previousModel.metrics?.['validation:recall']
    )

    if (currentF1 === 0 || previousF1 === 0) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#666666',
          }}
        >
          <FaMinus size={12} />
        </div>
      )
    }

    const improvement = currentF1 - previousF1
    const threshold = 0.001 // 0.1% threshold for meaningful change

    if (improvement > threshold) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#10b981',
          }}
        >
          <FaArrowUp size={12} />
        </div>
      )
    } else if (improvement < -threshold) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#dc3545',
          }}
        >
          <FaArrowDown size={12} />
        </div>
      )
    } else {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#666666',
          }}
        >
          <FaMinus size={12} />
        </div>
      )
    }
  }

  // Initialize sources from model data and current selections
  useEffect(() => {
    // Get the latest child model if available, otherwise use the parent model
    if (model?.parameters) {
      const modelTrainingSources = [
        ...(model?.parameters.train_metadata?.datasource || []),
        ...(model?.batch?.metadata_filter?.datasource || []),
      ]
      const modelValidationSources = [...(model?.parameters.validation_metadata?.datasource || [])]

      // If model has no training sources defined, fall back to global dataset sources
      const fallbackTrainingSources =
        modelTrainingSources.length > 0
          ? modelTrainingSources
          : model?.batch?.metadata_filter?.datasource || []

      // Combine model sources with current selections, removing duplicates
      setSelectedTrainingSources(Array.from(new Set([...fallbackTrainingSources])))
      setSelectedValidationSources(Array.from(new Set([...modelValidationSources])))
    }
  }, [
    model,
    model?.batch?.metadata_filter?.datasource,
    model?.parameters.train_metadata?.datasource,
    model?.parameters.validation_metadata?.datasource,
  ])

  const handleRetrain = async () => {
    if (!selectedModelId) {
      setErrorMessage('No model selected')
      setShowError(true)
      return
    }

    if (selectedTrainingSources.length + selectedValidationSources.length === 0) {
      setErrorMessage('No training sources available')
      setShowError(true)
      return
    }

    // Generate random suffix for fine-tuned model name
    const randomChars = Math.random().toString(36).substring(2, 4).toUpperCase()
    const newModelName = `${model?.name}-${randomChars}`

    // Combine existing sources with additional sources
    const allTrainingSources = selectedTrainingSources
    const allValidationSources = selectedValidationSources

    try {
      setActiveMainTab('training')
      await fineTuneModel({
        id: selectedModelId,
        epochs: 5,
        freeze: 10,
        name: newModelName,
        train_metadata_filter: { datasource: allTrainingSources },
        validation_metadata_filter: { datasource: allValidationSources },
      }).unwrap()
      // Stay on current view - the polling will automatically show the new training model
    } catch (error) {
      setErrorMessage(
        'Failed to fine-tune model: ' + (error as any)?.data?.message || 'Unknown Error'
      )
      setShowError(true)
    }
  }

  const { data: annoStats } = useGetAnnotationStatisticsQuery(
    {
      projectId: selectedProjectId || '',
      datasetId: project?.datasetid || '',
      metadata_filter:
        selectedTrainingSources.length > 0 ? { datasource: selectedTrainingSources } : undefined,
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  const totalTrainingAnnotations = Object.values(
    annoStats?.statistics?.annotations_per_class || {}
  ).reduce((sum, classData) => sum + (classData.count || 0), 0)

  // Calculate human reviewed data from batch results
  const originalUnlabeledCount = model?.batch?.total_content || 0
  const estimatedHumanReviewed = Math.max(
    0,
    originalUnlabeledCount - (annoStats?.statistics?.total_unannotated_images || 0)
  )

  // Readiness checks for fine-tuning
  const hasHumanReviewedData = estimatedHumanReviewed > 0
  const canStart = hasHumanReviewedData && !isRetraining && !isCurrentlyTraining
  const coveragePercent =
    originalUnlabeledCount > 0
      ? Math.round((estimatedHumanReviewed / originalUnlabeledCount) * 100)
      : 0
  const isIdealCoverage = coveragePercent >= 10
  const coverageStatus = hasHumanReviewedData
    ? isIdealCoverage
      ? 'ideal'
      : 'acceptable'
    : 'needs-review'

  return (
    <Container>
      {/* Simplified Header */}
      {/* <div
        style={{
          background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid rgba(147, 51, 234, 0.2)',
          textAlign: 'left',
          marginBottom: '2rem',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '1rem',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              color: '#ffffff',
            }}
          >
            <FaBrain />
          </div>
          <div>
            <h1
              style={{
                color: '#ffffff',
                fontSize: '1.5rem',
                fontWeight: '700',
                margin: '0',
              }}
            >
              Fine-Tune Model
            </h1>
            <p
              style={{
                color: '#b0b0b0',
                fontSize: '0.9rem',
                margin: '0.25rem 0 0 0',
              }}
            >
              Improve {model?.name || 'your model'} with human-reviewed data
            </p>
          </div>
        </div>
      </div> */}

      {/* Clean Organized Grid Layout */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          paddingBottom: '2rem',
        }}
      >
        {/* Top Row: 2-Column Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 400px',
            gap: '1.5rem',
            marginBottom: '1.5rem',
            alignItems: 'stretch',
          }}
        >
          {/* Main Tabbed Content Card */}
          <div
            style={{
              background: '#2a2a2a',
              borderRadius: '12px',
              padding: '1.5rem',
              border: '1px solid rgba(147, 51, 234, 0.2)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Main Tab Navigation */}
            <TabContainer>
              <TabNavigation style={{ marginBottom: '1.5rem' }}>
                <TabButton
                  $active={activeMainTab === 'model-data'}
                  onClick={() => setActiveMainTab('model-data')}
                >
                  <FaLayerGroup />
                  Model & Data
                </TabButton>
                <TabButton
                  $active={activeMainTab === 'training'}
                  onClick={() => setActiveMainTab('training')}
                >
                  <FaChartBar />
                  Training Progress
                </TabButton>
              </TabNavigation>

              <TabContent>
                {activeMainTab === 'model-data' && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '2rem',
                      height: '100%',
                    }}
                  >
                    {/* Model Performance Card */}
                    <div
                      style={{
                        background: '#333333',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        border: '1px solid rgba(147, 51, 234, 0.2)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          marginBottom: '1.5rem',
                          paddingBottom: '1rem',
                          borderBottom: '1px solid rgba(147, 51, 234, 0.2)',
                        }}
                      >
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            fontSize: '1rem',
                          }}
                        >
                          <FaChartBar />
                        </div>
                        <h3
                          style={{
                            color: '#ffffff',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            margin: '0',
                          }}
                        >
                          Model Performance
                        </h3>
                      </div>

                      {/* Review Progress */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '1rem',
                          padding: '1rem',
                          background: 'rgba(147, 51, 234, 0.1)',
                          borderRadius: '8px',
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <div
                            style={{
                              color: '#ffffff',
                              fontSize: '1.5rem',
                              fontWeight: '700',
                            }}
                          >
                            {estimatedHumanReviewed}
                          </div>
                          <div style={{ color: '#b0b0b0', fontSize: '0.8rem' }}>Reviewed</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div
                            style={{
                              color:
                                coverageStatus === 'ideal'
                                  ? '#10b981'
                                  : coverageStatus === 'acceptable'
                                  ? '#ffc107'
                                  : '#ffffff',
                              fontSize: '1.5rem',
                              fontWeight: '700',
                            }}
                          >
                            {coveragePercent}%
                          </div>
                          <div style={{ color: '#b0b0b0', fontSize: '0.8rem' }}>Coverage</div>
                        </div>
                        <div
                          style={{
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              background:
                                coverageStatus === 'ideal'
                                  ? '#10b981'
                                  : coverageStatus === 'acceptable'
                                  ? '#ffc107'
                                  : '#dc3545',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#ffffff',
                              margin: '0 auto',
                            }}
                          >
                            {coverageStatus === 'ideal' ? (
                              <FaCheck />
                            ) : coverageStatus === 'acceptable' ? (
                              <FaCheck />
                            ) : (
                              <FaExclamationCircle />
                            )}
                          </div>
                          <div
                            style={{
                              color:
                                coverageStatus === 'ideal'
                                  ? '#10b981'
                                  : coverageStatus === 'acceptable'
                                  ? '#ffc107'
                                  : '#dc3545',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              marginTop: '0.25rem',
                              textAlign: 'center',
                            }}
                          >
                            {coverageStatus === 'ideal'
                              ? 'Ideal'
                              : coverageStatus === 'acceptable'
                              ? 'Acceptable'
                              : 'Review Needed'}
                          </div>
                        </div>
                      </div>

                      {/* Performance Metrics */}
                      {model?.metrics && Object.keys(model.metrics).length > 0 ? (
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '0.75rem',
                          }}
                        >
                          {project?.annotation_type === 'bounding_box' ||
                          project?.annotation_type === 'oriented_bounding_box' ? (
                            <React.Fragment>
                              {model?.metrics?.['validation:precision'] &&
                              model?.metrics?.['validation:recall'] ? (
                                <div
                                  style={{
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    border: '1px solid rgba(59, 130, 246, 0.3)',
                                    borderRadius: '6px',
                                    padding: '1rem',
                                    textAlign: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#ffffff',
                                      fontSize: '1.3rem',
                                      fontWeight: '700',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    {calculateF1Score(
                                      model.metrics['validation:precision'],
                                      model.metrics['validation:recall']
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    F1 Score
                                  </div>
                                </div>
                              ) : null}
                              {model?.metrics?.['validation:mAP50'] ? (
                                <div
                                  style={{
                                    background: 'rgba(34, 197, 94, 0.1)',
                                    border: '1px solid rgba(34, 197, 94, 0.3)',
                                    borderRadius: '6px',
                                    padding: '1rem',
                                    textAlign: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#ffffff',
                                      fontSize: '1.3rem',
                                      fontWeight: '700',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    {typeof model.metrics['validation:mAP50'] === 'number'
                                      ? (model.metrics['validation:mAP50'] * 100).toFixed(1)
                                      : String(model.metrics['validation:mAP50']).slice(0, 4)}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    mAP50
                                  </div>
                                </div>
                              ) : null}
                              {model?.metrics?.['validation:precision'] ? (
                                <div
                                  style={{
                                    background: 'rgba(168, 85, 247, 0.1)',
                                    border: '1px solid rgba(168, 85, 247, 0.3)',
                                    borderRadius: '6px',
                                    padding: '1rem',
                                    textAlign: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#ffffff',
                                      fontSize: '1.3rem',
                                      fontWeight: '700',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    {typeof model.metrics['validation:precision'] === 'number'
                                      ? (model.metrics['validation:precision'] * 100).toFixed(1)
                                      : String(model.metrics['validation:precision']).slice(0, 4)}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Precision
                                  </div>
                                </div>
                              ) : null}
                              {model?.metrics?.['validation:recall'] ? (
                                <div
                                  style={{
                                    background: 'rgba(245, 158, 11, 0.1)',
                                    border: '1px solid rgba(245, 158, 11, 0.3)',
                                    borderRadius: '6px',
                                    padding: '1rem',
                                    textAlign: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#ffffff',
                                      fontSize: '1.3rem',
                                      fontWeight: '700',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    {typeof model.metrics['validation:recall'] === 'number'
                                      ? (model.metrics['validation:recall'] * 100).toFixed(1)
                                      : String(model.metrics['validation:recall']).slice(0, 4)}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Recall
                                  </div>
                                </div>
                              ) : null}
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              {model?.metrics?.['validation:accuracy'] ? (
                                <div
                                  style={{
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    border: '1px solid rgba(59, 130, 246, 0.3)',
                                    borderRadius: '6px',
                                    padding: '1rem',
                                    textAlign: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#ffffff',
                                      fontSize: '1.3rem',
                                      fontWeight: '700',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    {typeof model.metrics['validation:accuracy'] === 'number'
                                      ? (model.metrics['validation:accuracy'] * 100).toFixed(1)
                                      : String(model.metrics['validation:accuracy']).slice(0, 4)}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Accuracy
                                  </div>
                                </div>
                              ) : null}
                              {model?.metrics?.['validation:precision'] ? (
                                <div
                                  style={{
                                    background: 'rgba(34, 197, 94, 0.1)',
                                    border: '1px solid rgba(34, 197, 94, 0.3)',
                                    borderRadius: '6px',
                                    padding: '1rem',
                                    textAlign: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      color: '#ffffff',
                                      fontSize: '1.3rem',
                                      fontWeight: '700',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    {typeof model.metrics['validation:precision'] === 'number'
                                      ? (model.metrics['validation:precision'] * 100).toFixed(1)
                                      : String(model.metrics['validation:precision']).slice(0, 4)}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Precision
                                  </div>
                                </div>
                              ) : null}
                            </React.Fragment>
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100px',
                            color: '#666',
                            fontSize: '0.9rem',
                            fontStyle: 'italic',
                          }}
                        >
                          No performance metrics available
                        </div>
                      )}
                    </div>

                    {/* Dataset Configuration Card */}
                    <div
                      style={{
                        background: '#333333',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        border: '1px solid rgba(147, 51, 234, 0.2)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          marginBottom: '1.5rem',
                          paddingBottom: '1rem',
                          borderBottom: '1px solid rgba(147, 51, 234, 0.2)',
                        }}
                      >
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            fontSize: '1rem',
                          }}
                        >
                          <FaDatabase />
                        </div>
                        <h3
                          style={{
                            color: '#ffffff',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            margin: '0',
                          }}
                        >
                          Dataset Configuration
                        </h3>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1rem',
                          marginBottom: '1.5rem',
                        }}
                      >
                        <SelectedTrainingSourceCard
                          selectedSources={selectedTrainingSources}
                          onSourcesChange={setSelectedTrainingSources}
                          readOnly={isRetraining}
                        />

                        <SelectedValidationSourceCard
                          selectedSources={selectedValidationSources}
                          onSourcesChange={setSelectedValidationSources}
                          readOnly={isRetraining}
                        />
                      </div>

                      {totalTrainingAnnotations > 0 && (
                        <div
                          style={{
                            padding: '1.25rem',
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            borderRadius: '8px',
                            textAlign: 'center',
                            marginTop: '2.75rem',
                          }}
                        >
                          <div
                            style={{
                              color: '#10b981',
                              fontSize: '1.2rem',
                              fontWeight: '600',
                              marginBottom: '0.5rem',
                            }}
                          >
                            {totalTrainingAnnotations.toLocaleString()}
                          </div>
                          <div
                            style={{
                              color: '#10b981',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              marginBottom: '0.25rem',
                            }}
                          >
                            Training Annotations Available
                          </div>
                          <div style={{ color: '#b0b0b0', fontSize: '0.8rem' }}>
                            Ready to fine-tune with{' '}
                            {sortedChildModels.length > 0 ? 'additional' : 'existing'} training data
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeMainTab === 'training' && (
                  <div>
                    {/* Training Status Overview */}
                    {isCurrentlyTraining ? (
                      <div
                        style={{
                          background: 'rgba(255, 193, 7, 0.1)',
                          border: '1px solid rgba(255, 193, 7, 0.2)',
                          borderRadius: '8px',
                          padding: '1.5rem',
                          marginBottom: '1.5rem',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            marginBottom: '1rem',
                          }}
                        >
                          <FaSpinner
                            style={{
                              color: '#ffc107',
                              animation: 'spin 1s linear infinite',
                            }}
                          />
                          <h4
                            style={{
                              color: '#ffffff',
                              margin: '0',
                              fontSize: '1rem',
                            }}
                          >
                            Current Training Status
                          </h4>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            marginBottom: '1rem',
                          }}
                        >
                          <span style={{ color: '#ffc107', fontWeight: '500' }}>
                            Training in Progress...
                          </span>
                          {(latestModel as any)?.training_stats?.bounding_box && (
                            <>
                              <div
                                style={{
                                  background:
                                    ((latestModel as any).training_stats.bounding_box
                                      .training_upload_data_progress || 0) === 0
                                      ? 'rgba(220, 53, 69, 0.1)'
                                      : ((latestModel as any).training_stats.bounding_box
                                          .training_upload_data_progress || 0) === 1
                                      ? 'rgba(16, 185, 129, 0.1)'
                                      : 'rgba(255, 193, 7, 0.1)',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  color:
                                    ((latestModel as any).training_stats.bounding_box
                                      .training_upload_data_progress || 0) === 0
                                      ? '#dc3545'
                                      : ((latestModel as any).training_stats.bounding_box
                                          .training_upload_data_progress || 0) === 1
                                      ? '#10b981'
                                      : '#ffc107',
                                  fontSize: '0.8rem',
                                }}
                              >
                                Training Data Upload:{' '}
                                {Math.round(
                                  ((latestModel as any).training_stats.bounding_box
                                    .training_upload_data_progress || 0) * 100
                                )}
                                %
                              </div>
                              <div
                                style={{
                                  background:
                                    ((latestModel as any).training_stats.bounding_box
                                      .validation_upload_data_progress || 0) === 0
                                      ? 'rgba(220, 53, 69, 0.1)'
                                      : ((latestModel as any).training_stats.bounding_box
                                          .validation_upload_data_progress || 0) === 1
                                      ? 'rgba(16, 185, 129, 0.1)'
                                      : 'rgba(255, 193, 7, 0.1)',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  color:
                                    ((latestModel as any).training_stats.bounding_box
                                      .validation_upload_data_progress || 0) === 0
                                      ? '#dc3545'
                                      : ((latestModel as any).training_stats.bounding_box
                                          .validation_upload_data_progress || 0) === 1
                                      ? '#10b981'
                                      : '#ffc107',
                                  fontSize: '0.8rem',
                                }}
                              >
                                Validation Data Upload:{' '}
                                {Math.round(
                                  ((latestModel as any).training_stats.bounding_box
                                    .validation_upload_data_progress || 0) * 100
                                )}
                                %
                              </div>
                              <div
                                style={{
                                  background:
                                    (latestModel?.state?.toUpperCase?.() === 'TRAINING' &&
                                    ((latestModel as any).training_stats.bounding_box
                                      .training_progress || 0) === 1
                                      ? 0.9
                                      : (latestModel as any).training_stats.bounding_box
                                          .training_progress || 0) === 0
                                      ? 'rgba(220, 53, 69, 0.1)'
                                      : (latestModel?.state?.toUpperCase?.() === 'TRAINING' &&
                                        ((latestModel as any).training_stats.bounding_box
                                          .training_progress || 0) === 1
                                          ? 0.9
                                          : (latestModel as any).training_stats.bounding_box
                                              .training_progress || 0) === 1
                                      ? 'rgba(16, 185, 129, 0.1)'
                                      : 'rgba(255, 193, 7, 0.1)',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  color:
                                    (latestModel?.state?.toUpperCase?.() === 'TRAINING' &&
                                    ((latestModel as any).training_stats.bounding_box
                                      .training_progress || 0) === 1
                                      ? 0.9
                                      : (latestModel as any).training_stats.bounding_box
                                          .training_progress || 0) === 0
                                      ? '#dc3545'
                                      : (latestModel?.state?.toUpperCase?.() === 'TRAINING' &&
                                        ((latestModel as any).training_stats.bounding_box
                                          .training_progress || 0) === 1
                                          ? 0.9
                                          : (latestModel as any).training_stats.bounding_box
                                              .training_progress || 0) === 1
                                      ? '#10b981'
                                      : '#ffc107',
                                  fontSize: '0.8rem',
                                }}
                              >
                                Training Progress:{' '}
                                {Math.round(
                                  (latestModel?.state?.toUpperCase?.() === 'TRAINING' &&
                                  ((latestModel as any).training_stats.bounding_box
                                    .training_progress || 0) === 1
                                    ? 0.9
                                    : (latestModel as any).training_stats.bounding_box
                                        .training_progress || 0) * 100
                                )}
                                %
                              </div>
                            </>
                          )}
                          <div style={{ color: '#b0b0b0', fontSize: '0.85rem' }}>
                            Started:{' '}
                            {latestModel?.train_started_at
                              ? new Date(latestModel.train_started_at).toLocaleString()
                              : 'N/A'}
                          </div>
                        </div>

                        {/* Key Training Metrics */}
                        {latestModel?.metrics && (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns:
                                project?.annotation_type === 'bounding_box' ||
                                project?.annotation_type === 'oriented_bounding_box'
                                  ? 'repeat(4, 1fr)'
                                  : 'repeat(3, 1fr)',
                              gap: '1rem',
                              marginTop: '1rem',
                            }}
                          >
                            {project?.annotation_type === 'bounding_box' ||
                            project?.annotation_type === 'oriented_bounding_box' ? (
                              <>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#3b82f6',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {calculateF1Score(
                                      latestModel.metrics['validation:precision'],
                                      latestModel.metrics['validation:recall']
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    F1 Score
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#10b981',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:mAP50']
                                      ? typeof latestModel.metrics['validation:mAP50'] === 'number'
                                        ? (latestModel.metrics['validation:mAP50'] * 100).toFixed(1)
                                        : String(latestModel.metrics['validation:mAP50']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    mAP50
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#a855f7',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:precision']
                                      ? typeof latestModel.metrics['validation:precision'] ===
                                        'number'
                                        ? (
                                            latestModel.metrics['validation:precision'] * 100
                                          ).toFixed(1)
                                        : String(latestModel.metrics['validation:precision']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Precision
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#f59e0b',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:recall']
                                      ? typeof latestModel.metrics['validation:recall'] === 'number'
                                        ? (latestModel.metrics['validation:recall'] * 100).toFixed(
                                            1
                                          )
                                        : String(latestModel.metrics['validation:recall']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Recall
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#3b82f6',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:accuracy']
                                      ? typeof latestModel.metrics['validation:accuracy'] ===
                                        'number'
                                        ? (
                                            latestModel.metrics['validation:accuracy'] * 100
                                          ).toFixed(1)
                                        : String(latestModel.metrics['validation:accuracy']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Accuracy
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#a855f7',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:precision']
                                      ? typeof latestModel.metrics['validation:precision'] ===
                                        'number'
                                        ? (
                                            latestModel.metrics['validation:precision'] * 100
                                          ).toFixed(1)
                                        : String(latestModel.metrics['validation:precision']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Precision
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#f59e0b',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:recall']
                                      ? typeof latestModel.metrics['validation:recall'] === 'number'
                                        ? (latestModel.metrics['validation:recall'] * 100).toFixed(
                                            1
                                          )
                                        : String(latestModel.metrics['validation:recall']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Recall
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ) : sortedChildModels.length > 0 ? (
                      <div
                        style={{
                          background: 'rgba(16, 185, 129, 0.1)',
                          border: '1px solid rgba(16, 185, 129, 0.2)',
                          borderRadius: '8px',
                          padding: '1.5rem',
                          marginBottom: '1.5rem',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            marginBottom: '1rem',
                          }}
                        >
                          <FaCheckCircle style={{ color: '#10b981' }} />
                          <h4
                            style={{
                              color: '#ffffff',
                              margin: '0',
                              fontSize: '1rem',
                            }}
                          >
                            Latest Training Complete
                          </h4>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            marginBottom: '1rem',
                          }}
                        >
                          <span style={{ color: '#10b981', fontWeight: '500' }}>
                            {latestModel?.name || 'Training completed successfully'}
                          </span>
                          <div style={{ color: '#b0b0b0', fontSize: '0.85rem' }}>
                            Completed:{' '}
                            {latestModel?.train_ended_at &&
                            latestModel.train_ended_at !== '0001-01-01T00:00:00Z'
                              ? new Date(latestModel.train_ended_at).toLocaleString()
                              : latestModel?.updated_at
                              ? new Date(latestModel.updated_at).toLocaleString()
                              : 'Recently'}
                          </div>
                        </div>

                        {/* Key Training Metrics */}
                        {latestModel?.metrics && (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns:
                                project?.annotation_type === 'bounding_box' ||
                                project?.annotation_type === 'oriented_bounding_box'
                                  ? 'repeat(4, 1fr)'
                                  : 'repeat(3, 1fr)',
                              gap: '1rem',
                              marginTop: '1rem',
                            }}
                          >
                            {project?.annotation_type === 'bounding_box' ||
                            project?.annotation_type === 'oriented_bounding_box' ? (
                              <>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#3b82f6',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {calculateF1Score(
                                      latestModel.metrics['validation:precision'],
                                      latestModel.metrics['validation:recall']
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    F1 Score
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#10b981',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:mAP50']
                                      ? typeof latestModel.metrics['validation:mAP50'] === 'number'
                                        ? (latestModel.metrics['validation:mAP50'] * 100).toFixed(1)
                                        : String(latestModel.metrics['validation:mAP50']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    mAP50
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#a855f7',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:precision']
                                      ? typeof latestModel.metrics['validation:precision'] ===
                                        'number'
                                        ? (
                                            latestModel.metrics['validation:precision'] * 100
                                          ).toFixed(1)
                                        : String(latestModel.metrics['validation:precision']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Precision
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#f59e0b',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:recall']
                                      ? typeof latestModel.metrics['validation:recall'] === 'number'
                                        ? (latestModel.metrics['validation:recall'] * 100).toFixed(
                                            1
                                          )
                                        : String(latestModel.metrics['validation:recall']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Recall
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#3b82f6',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:accuracy']
                                      ? typeof latestModel.metrics['validation:accuracy'] ===
                                        'number'
                                        ? (
                                            latestModel.metrics['validation:accuracy'] * 100
                                          ).toFixed(1)
                                        : String(latestModel.metrics['validation:accuracy']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Accuracy
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#a855f7',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:precision']
                                      ? typeof latestModel.metrics['validation:precision'] ===
                                        'number'
                                        ? (
                                            latestModel.metrics['validation:precision'] * 100
                                          ).toFixed(1)
                                        : String(latestModel.metrics['validation:precision']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Precision
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div
                                    style={{
                                      color: '#f59e0b',
                                      fontSize: '1.5rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {latestModel.metrics['validation:recall']
                                      ? typeof latestModel.metrics['validation:recall'] === 'number'
                                        ? (latestModel.metrics['validation:recall'] * 100).toFixed(
                                            1
                                          )
                                        : String(latestModel.metrics['validation:recall']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    Recall
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ) : null}

                    {/* Training History Table */}
                    <div
                      style={{
                        background: '#333333',
                        borderRadius: '8px',
                        border: '1px solid rgba(147, 51, 234, 0.1)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '1rem',
                          borderBottom: '1px solid rgba(147, 51, 234, 0.2)',
                        }}
                      >
                        <FaHistory style={{ color: '#9333ea' }} />
                        <h4
                          style={{
                            color: '#ffffff',
                            margin: '0',
                            fontSize: '1rem',
                          }}
                        >
                          Training History ({sortedChildModels.length + 1} iterations)
                        </h4>
                      </div>

                      <div style={{ padding: '0' }}>
                        {/* Table Header */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              project?.annotation_type === 'bounding_box' ||
                              project?.annotation_type === 'oriented_bounding_box'
                                ? '100px 1fr 100px 80px 80px 80px 80px 60px 100px'
                                : '100px 1fr 100px 80px 80px 80px 60px 100px',
                            gap: '0.5rem',
                            padding: '0.75rem 1rem',
                            background: 'rgba(147, 51, 234, 0.1)',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: '#b0b0b0',
                            textTransform: 'uppercase',
                          }}
                        >
                          <div>Iteration</div>
                          <div>Model Name</div>
                          <div>Status</div>
                          {project?.annotation_type === 'bounding_box' ||
                          project?.annotation_type === 'oriented_bounding_box' ? (
                            <>
                              <div style={{ textAlign: 'center' }}>F1 Score</div>
                              <div style={{ textAlign: 'center' }}>mAP50</div>
                              <div style={{ textAlign: 'center' }}>Precision</div>
                              <div style={{ textAlign: 'center' }}>Recall</div>
                            </>
                          ) : (
                            <>
                              <div style={{ textAlign: 'center' }}>Accuracy</div>
                              <div style={{ textAlign: 'center' }}>Precision</div>
                              <div style={{ textAlign: 'center' }}>Recall</div>
                            </>
                          )}
                          <div style={{ textAlign: 'center' }}>Trend</div>
                          <div>Created</div>
                        </div>

                        {/* Base Model Row */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              project?.annotation_type === 'bounding_box' ||
                              project?.annotation_type === 'oriented_bounding_box'
                                ? '100px 1fr 100px 80px 80px 80px 80px 60px 100px'
                                : '100px 1fr 100px 80px 80px 80px 60px 100px',
                            gap: '0.5rem',
                            padding: '0.75rem 1rem',
                            borderBottom: '1px solid rgba(147, 51, 234, 0.1)',
                            fontSize: '0.8rem',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            style={{
                              color: '#9333ea',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                            }}
                          >
                            <FaTrophy />
                            Base
                          </div>
                          <div style={{ color: '#ffffff', fontWeight: '500' }}>{model?.name}</div>
                          <div>
                            <span
                              style={{
                                background: 'rgba(16, 185, 129, 0.2)',
                                color: '#10b981',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.65rem',
                                fontWeight: '500',
                              }}
                            >
                              Original
                            </span>
                          </div>
                          {project?.annotation_type === 'bounding_box' ||
                          project?.annotation_type === 'oriented_bounding_box' ? (
                            <>
                              <div
                                style={{
                                  color: '#b0b0b0',
                                  textAlign: 'center',
                                }}
                              >
                                {calculateF1Score(
                                  model?.metrics?.['validation:precision'],
                                  model?.metrics?.['validation:recall']
                                )}
                              </div>
                              <div
                                style={{
                                  color: '#b0b0b0',
                                  textAlign: 'center',
                                }}
                              >
                                {model?.metrics?.['validation:mAP50']
                                  ? typeof model.metrics['validation:mAP50'] === 'number'
                                    ? (model.metrics['validation:mAP50'] * 100).toFixed(1)
                                    : String(model.metrics['validation:mAP50']).slice(0, 4)
                                  : '-'}
                              </div>
                              <div
                                style={{
                                  color: '#b0b0b0',
                                  textAlign: 'center',
                                }}
                              >
                                {model?.metrics?.['validation:precision']
                                  ? typeof model.metrics['validation:precision'] === 'number'
                                    ? (model.metrics['validation:precision'] * 100).toFixed(1)
                                    : String(model.metrics['validation:precision']).slice(0, 4)
                                  : '-'}
                              </div>
                              <div
                                style={{
                                  color: '#b0b0b0',
                                  textAlign: 'center',
                                }}
                              >
                                {model?.metrics?.['validation:recall']
                                  ? typeof model.metrics['validation:recall'] === 'number'
                                    ? (model.metrics['validation:recall'] * 100).toFixed(1)
                                    : String(model.metrics['validation:recall']).slice(0, 4)
                                  : '-'}
                              </div>
                            </>
                          ) : (
                            <>
                              <div
                                style={{
                                  color: '#b0b0b0',
                                  textAlign: 'center',
                                }}
                              >
                                {model?.metrics?.['validation:accuracy']
                                  ? typeof model.metrics['validation:accuracy'] === 'number'
                                    ? (model.metrics['validation:accuracy'] * 100).toFixed(1)
                                    : String(model.metrics['validation:accuracy']).slice(0, 4)
                                  : '-'}
                              </div>
                              <div
                                style={{
                                  color: '#b0b0b0',
                                  textAlign: 'center',
                                }}
                              >
                                {model?.metrics?.['validation:precision']
                                  ? typeof model.metrics['validation:precision'] === 'number'
                                    ? (model.metrics['validation:precision'] * 100).toFixed(1)
                                    : String(model.metrics['validation:precision']).slice(0, 4)
                                  : '-'}
                              </div>
                              <div
                                style={{
                                  color: '#b0b0b0',
                                  textAlign: 'center',
                                }}
                              >
                                {model?.metrics?.['validation:recall']
                                  ? typeof model.metrics['validation:recall'] === 'number'
                                    ? (model.metrics['validation:recall'] * 100).toFixed(1)
                                    : String(model.metrics['validation:recall']).slice(0, 4)
                                  : '-'}
                              </div>
                            </>
                          )}
                          <div>{model && renderTrendIndicator(model, null)}</div>
                          <div style={{ color: '#b0b0b0', fontSize: '0.75rem' }}>
                            {model?.created_at
                              ? new Date(model.created_at).toLocaleDateString()
                              : 'N/A'}
                          </div>
                        </div>

                        {/* Child Model Rows */}
                        {sortedChildModels.map((childModel, index) => {
                          const trainingStates = [
                            'training',
                            'pending',
                            'queued',
                            'in_progress',
                            'running',
                          ]
                          const isChildTraining = trainingStates.includes(
                            childModel.state?.toLowerCase() || ''
                          )

                          // Extract latest epoch metrics if training
                          let latestEpochPrecision: number | null | undefined = undefined
                          let latestEpochRecall: number | null | undefined = undefined
                          let latestEpochMap50: number | null | undefined = undefined
                          let latestEpochAccuracy: number | null | undefined = undefined

                          if (isChildTraining) {
                            if (
                              project?.annotation_type === 'bounding_box' ||
                              project?.annotation_type === 'oriented_bounding_box'
                            ) {
                              const metricsMap =
                                childModel.training_stats?.bounding_box?.training_job_metrics
                              const firstKey = metricsMap ? Object.keys(metricsMap)[0] : undefined
                              const entries: TrainingJobEntryBBox[] = firstKey
                                ? metricsMap?.[firstKey]?.training_job_entries || []
                                : []
                              const last: TrainingJobEntryBBox | undefined =
                                entries.length > 0 ? entries[entries.length - 1] : undefined
                              latestEpochPrecision = last?.metrics_precision ?? undefined
                              latestEpochRecall = last?.metrics_recall ?? undefined
                              latestEpochMap50 = last?.metrics_map_50 ?? undefined
                            } else {
                              const metricsMap =
                                childModel.training_stats?.classification?.training_job_metrics
                              const firstKey = metricsMap ? Object.keys(metricsMap)[0] : undefined
                              const entries: TrainingJobEntryClassification[] = firstKey
                                ? metricsMap?.[firstKey]?.training_job_entries || []
                                : []
                              const last: TrainingJobEntryClassification | undefined =
                                entries.length > 0 ? entries[entries.length - 1] : undefined
                              latestEpochAccuracy = last?.accuracy ?? undefined
                            }
                          }

                          return (
                            <div
                              key={childModel.id}
                              style={{
                                display: 'grid',
                                gridTemplateColumns:
                                  project?.annotation_type === 'bounding_box' ||
                                  project?.annotation_type === 'oriented_bounding_box'
                                    ? '100px 1fr 100px 80px 80px 80px 80px 60px 100px'
                                    : '100px 1fr 100px 80px 80px 80px 60px 100px',
                                gap: '0.5rem',
                                padding: '0.75rem 1rem',
                                borderBottom:
                                  index < sortedChildModels.length - 1
                                    ? '1px solid rgba(147, 51, 234, 0.1)'
                                    : 'none',
                                fontSize: '0.8rem',
                                alignItems: 'center',
                              }}
                            >
                              <div
                                style={{
                                  color: index === 0 ? '#ffc107' : '#b0b0b0',
                                  fontWeight: '600',
                                }}
                              >
                                #{sortedChildModels.length - index}
                                {index === 0 && (
                                  <div
                                    style={{
                                      fontSize: '0.65rem',
                                      fontWeight: 'normal',
                                    }}
                                  >
                                    (Latest)
                                  </div>
                                )}
                              </div>
                              <div style={{ color: '#ffffff', fontWeight: '500' }}>
                                {childModel.name}
                              </div>
                              <div>
                                <span
                                  style={{
                                    background:
                                      childModel.state?.toLowerCase() === 'training'
                                        ? 'rgba(255, 193, 7, 0.2)'
                                        : childModel.state?.toLowerCase() === 'error'
                                        ? 'rgba(220, 53, 69, 0.2)'
                                        : 'rgba(16, 185, 129, 0.2)',
                                    color:
                                      childModel.state?.toLowerCase() === 'training'
                                        ? '#ffc107'
                                        : childModel.state?.toLowerCase() === 'error'
                                        ? '#dc3545'
                                        : '#10b981',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.65rem',
                                    fontWeight: '500',
                                  }}
                                >
                                  {childModel.state?.toLowerCase() === 'training'
                                    ? 'Training'
                                    : childModel.state?.toLowerCase() === 'error'
                                    ? 'Failed'
                                    : 'Complete'}
                                </span>
                              </div>
                              {project?.annotation_type === 'bounding_box' ||
                              project?.annotation_type === 'oriented_bounding_box' ? (
                                <>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {isChildTraining
                                      ? calculateF1Score(
                                          latestEpochPrecision ?? undefined,
                                          latestEpochRecall ?? undefined
                                        )
                                      : calculateF1Score(
                                          childModel.metrics?.['validation:precision'],
                                          childModel.metrics?.['validation:recall']
                                        )}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {isChildTraining
                                      ? latestEpochMap50 != null
                                        ? (Number(latestEpochMap50) * 100).toFixed(1)
                                        : '-'
                                      : childModel.metrics?.['validation:mAP50']
                                      ? typeof childModel.metrics['validation:mAP50'] === 'number'
                                        ? (childModel.metrics['validation:mAP50'] * 100).toFixed(1)
                                        : String(childModel.metrics['validation:mAP50']).slice(0, 4)
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {isChildTraining
                                      ? latestEpochPrecision != null
                                        ? (Number(latestEpochPrecision) * 100).toFixed(1)
                                        : '-'
                                      : childModel.metrics?.['validation:precision']
                                      ? typeof childModel.metrics['validation:precision'] ===
                                        'number'
                                        ? (
                                            childModel.metrics['validation:precision'] * 100
                                          ).toFixed(1)
                                        : String(childModel.metrics['validation:precision']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {isChildTraining
                                      ? latestEpochRecall != null
                                        ? (Number(latestEpochRecall) * 100).toFixed(1)
                                        : '-'
                                      : childModel.metrics?.['validation:recall']
                                      ? typeof childModel.metrics['validation:recall'] === 'number'
                                        ? (childModel.metrics['validation:recall'] * 100).toFixed(1)
                                        : String(childModel.metrics['validation:recall']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {isChildTraining
                                      ? latestEpochAccuracy != null
                                        ? (Number(latestEpochAccuracy) * 100).toFixed(1)
                                        : '-'
                                      : childModel.metrics?.['validation:accuracy']
                                      ? typeof childModel.metrics['validation:accuracy'] ===
                                        'number'
                                        ? (childModel.metrics['validation:accuracy'] * 100).toFixed(
                                            1
                                          )
                                        : String(childModel.metrics['validation:accuracy']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {isChildTraining
                                      ? '-'
                                      : childModel.metrics?.['validation:precision']
                                      ? typeof childModel.metrics['validation:precision'] ===
                                        'number'
                                        ? (
                                            childModel.metrics['validation:precision'] * 100
                                          ).toFixed(1)
                                        : String(childModel.metrics['validation:precision']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                  <div
                                    style={{
                                      color: '#b0b0b0',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {isChildTraining
                                      ? '-'
                                      : childModel.metrics?.['validation:recall']
                                      ? typeof childModel.metrics['validation:recall'] === 'number'
                                        ? (childModel.metrics['validation:recall'] * 100).toFixed(1)
                                        : String(childModel.metrics['validation:recall']).slice(
                                            0,
                                            4
                                          )
                                      : '-'}
                                  </div>
                                </>
                              )}
                              <div>
                                {renderTrendIndicator(
                                  childModel,
                                  index === sortedChildModels.length - 1
                                    ? model || null
                                    : sortedChildModels[index + 1] || null
                                )}
                              </div>
                              <div style={{ color: '#b0b0b0', fontSize: '0.75rem' }}>
                                {new Date(childModel.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </TabContent>
            </TabContainer>
          </div>

          {/* Fine-Tune Action Card */}
          <div
            style={{
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
              borderRadius: '12px',
              padding: '1.5rem',
              border: '2px solid rgba(147, 51, 234, 0.3)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <h3
                    style={{
                      color: '#ffffff',
                      fontSize: '1.2rem',
                      fontWeight: '600',
                      margin: '0 0 0.75rem 0',
                    }}
                  >
                    Ready to Fine-Tune?
                  </h3>
                  <p
                    style={{
                      color: '#b0b0b0',
                      fontSize: '0.9rem',
                      margin: '0',
                      lineHeight: '1.5',
                    }}
                  >
                    {isCurrentlyTraining
                      ? 'Training session in progress. Please wait for completion.'
                      : hasHumanReviewedData
                      ? `Fine-tune with ${estimatedHumanReviewed} human-reviewed images.`
                      : 'Review AI predictions first to create training data.'}
                  </p>
                </div>

                {/* Readiness Status */}
                <div
                  style={{
                    background: isCurrentlyTraining
                      ? 'rgba(255, 193, 7, 0.1)'
                      : coverageStatus === 'ideal'
                      ? 'rgba(16, 185, 129, 0.1)'
                      : coverageStatus === 'acceptable'
                      ? 'rgba(255, 193, 7, 0.1)'
                      : 'rgba(220, 53, 69, 0.1)',
                    border: `1px solid ${
                      isCurrentlyTraining
                        ? 'rgba(255, 193, 7, 0.3)'
                        : coverageStatus === 'ideal'
                        ? 'rgba(16, 185, 129, 0.3)'
                        : coverageStatus === 'acceptable'
                        ? 'rgba(255, 193, 7, 0.3)'
                        : 'rgba(220, 53, 69, 0.3)'
                    }`,
                    borderRadius: '8px',
                    padding: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '2rem',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: isCurrentlyTraining
                        ? '#ffc107'
                        : coverageStatus === 'ideal'
                        ? '#10b981'
                        : coverageStatus === 'acceptable'
                        ? '#ffc107'
                        : '#dc3545',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      fontSize: '1rem',
                      flexShrink: 0,
                    }}
                  >
                    {isCurrentlyTraining ? (
                      <FaSpinner className="spinner" />
                    ) : coverageStatus === 'needs-review' ? (
                      <FaExclamationCircle />
                    ) : (
                      <FaCheckCircle />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        color: isCurrentlyTraining
                          ? '#ffc107'
                          : coverageStatus === 'ideal'
                          ? '#10b981'
                          : coverageStatus === 'acceptable'
                          ? '#ffc107'
                          : '#dc3545',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                      }}
                    >
                      {isCurrentlyTraining
                        ? 'Training in Progress'
                        : coverageStatus === 'ideal'
                        ? 'Ideal Coverage'
                        : coverageStatus === 'acceptable'
                        ? 'Acceptable Coverage'
                        : 'Review Needed'}
                    </div>
                    <div
                      style={{
                        color: '#b0b0b0',
                        fontSize: '0.8rem',
                        lineHeight: '1.3',
                      }}
                    >
                      {isCurrentlyTraining
                        ? 'Please wait for the current training session to complete'
                        : hasHumanReviewedData
                        ? `${estimatedHumanReviewed} images reviewed (${coveragePercent}% of batch)${
                            coveragePercent >= 10
                              ? ' - Great coverage!'
                              : ' - Consider reviewing more for optimal results'
                          }`
                        : 'Complete the Review phase to enable fine-tuning (10%+ coverage recommended)'}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 'auto' }}>
                {/* Fine-Tune Button */}
                <FineTuneButton
                  $isRetraining={isRetraining || isCurrentlyTraining}
                  $canStart={canStart}
                  onClick={handleRetrain}
                  disabled={!canStart}
                >
                  {isRetraining || isCurrentlyTraining ? (
                    <>
                      <FaSpinner className="spinner" />
                      {isCurrentlyTraining ? 'Training in Progress...' : 'Starting Training...'}
                    </>
                  ) : (
                    <>
                      <FaBrain />
                      Start Fine-Tuning
                    </>
                  )}
                </FineTuneButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={errorMessage}
        title="Fine-Tuning Error"
      />
    </Container>
  )
}

export default FineTunePhase
